import { useState, useEffect } from "react";
import { toast } from "sonner";
import { API, useAuth } from "@/App";
import {
  FileText,
  Upload,
  Loader2,
  Plus,
  Trash2,
  Eye,
  EyeOff,
  Share2,
  DollarSign,
  File,
  X,
  Download,
  Lock,
  Unlock,
  Building2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function PurchaseOrderManager({ entityType, entityId, entityName }) {
  const { token, user } = useAuth();
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddPO, setShowAddPO] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedPO, setSelectedPO] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  
  const [formData, setFormData] = useState({
    po_number: "",
    hospital_name: "",
    amount: "",
    description: "",
    share_with_hospital: false
  });

  const isSuperAdmin = user?.role === "super_admin";

  useEffect(() => {
    fetchPurchaseOrders();
  }, [entityId]);

  const fetchPurchaseOrders = async () => {
    try {
      const res = await fetch(
        `${API}/admin/purchase-orders?entity_type=${entityType}&entity_id=${entityId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.ok) {
        const data = await res.json();
        setPurchaseOrders(data.purchase_orders || []);
      }
    } catch (err) {
      toast.error("Failed to load purchase orders");
    } finally {
      setLoading(false);
    }
  };

  const handleAddPO = async () => {
    if (!formData.po_number) {
      toast.error("PO number is required");
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${API}/admin/purchase-orders`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          entity_type: entityType,
          entity_id: entityId,
          ...formData,
          amount: formData.amount ? parseFloat(formData.amount) : null
        })
      });

      if (res.ok) {
        toast.success("Purchase order added");
        setShowAddPO(false);
        setFormData({
          po_number: "",
          hospital_name: "",
          amount: "",
          description: "",
          share_with_hospital: false
        });
        fetchPurchaseOrders();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to add PO");
      }
    } catch (err) {
      toast.error("Failed to add purchase order");
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleShare = async (po) => {
    try {
      const res = await fetch(
        `${API}/admin/purchase-orders/${po.id}/share?share=${!po.share_with_hospital}`,
        {
          method: "PUT",
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (res.ok) {
        toast.success(po.share_with_hospital ? "PO hidden from hospital" : "PO shared with hospital");
        fetchPurchaseOrders();
      } else {
        toast.error("Failed to update sharing");
      }
    } catch (err) {
      toast.error("Failed to update sharing");
    }
  };

  const handleDelete = async () => {
    if (!selectedPO) return;

    setSubmitting(true);
    try {
      const res = await fetch(`${API}/admin/purchase-orders/${selectedPO.id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        toast.success("Purchase order deleted");
        setShowDeleteConfirm(false);
        setSelectedPO(null);
        fetchPurchaseOrders();
      } else {
        toast.error("Failed to delete PO");
      }
    } catch (err) {
      toast.error("Failed to delete purchase order");
    } finally {
      setSubmitting(false);
    }
  };

  const handleFileUpload = async (poId, file) => {
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);

      const res = await fetch(`${API}/admin/purchase-orders/${poId}/upload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });

      if (res.ok) {
        toast.success("File uploaded successfully");
        fetchPurchaseOrders();
      } else {
        toast.error("Failed to upload file");
      }
    } catch (err) {
      toast.error("Failed to upload file");
    } finally {
      setUploading(false);
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return "-";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD"
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FileText className="w-5 h-5 text-blue-600" />
          <h3 className="font-semibold text-slate-900">Purchase Orders</h3>
          <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
            {purchaseOrders.length}
          </span>
        </div>
        <Button size="sm" onClick={() => setShowAddPO(true)}>
          <Plus className="w-4 h-4 mr-1" />
          Add PO
        </Button>
      </div>

      {/* Info Banner */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
        <Lock className="w-4 h-4 inline mr-2" />
        Purchase orders are <strong>private by default</strong>. Use the share button to make them visible to hospitals.
      </div>

      {/* PO List */}
      {purchaseOrders.length === 0 ? (
        <div className="text-center py-8 text-slate-500 bg-slate-50 rounded-lg">
          <FileText className="w-10 h-10 mx-auto mb-2 text-slate-300" />
          <p>No purchase orders yet</p>
          <p className="text-xs mt-1">Click "Add PO" to create one</p>
        </div>
      ) : (
        <div className="space-y-3">
          {purchaseOrders.map((po) => (
            <div
              key={po.id}
              className="bg-white border border-slate-200 rounded-xl p-4 hover:shadow-sm transition-shadow"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="font-mono font-bold text-slate-900 text-lg">
                      {po.po_number}
                    </span>
                    {po.share_with_hospital ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-emerald-100 text-emerald-700">
                        <Eye className="w-3 h-3" />
                        Shared
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-slate-100 text-slate-600">
                        <EyeOff className="w-3 h-3" />
                        Private
                      </span>
                    )}
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    {po.hospital_name && (
                      <div>
                        <p className="text-slate-500 text-xs">Hospital</p>
                        <p className="text-slate-700">{po.hospital_name}</p>
                      </div>
                    )}
                    {po.amount && (
                      <div>
                        <p className="text-slate-500 text-xs">Amount</p>
                        <p className="text-slate-900 font-medium">{formatCurrency(po.amount)}</p>
                      </div>
                    )}
                    <div>
                      <p className="text-slate-500 text-xs">Created</p>
                      <p className="text-slate-700">
                        {new Date(po.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <div>
                      <p className="text-slate-500 text-xs">Files</p>
                      <p className="text-slate-700">{po.files?.length || 0} attached</p>
                    </div>
                  </div>

                  {po.description && (
                    <p className="text-sm text-slate-600 mt-2 border-t border-slate-100 pt-2">
                      {po.description}
                    </p>
                  )}

                  {/* Files */}
                  {po.files?.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {po.files.map((file) => (
                        <div
                          key={file.id}
                          className="flex items-center gap-2 px-2 py-1 bg-slate-100 rounded text-xs"
                        >
                          <File className="w-3 h-3 text-slate-500" />
                          <span className="text-slate-700">{file.filename}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1 ml-4">
                  {/* File Upload */}
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      className="hidden"
                      onChange={(e) => {
                        if (e.target.files?.[0]) {
                          handleFileUpload(po.id, e.target.files[0]);
                        }
                      }}
                      accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                    />
                    <Button size="sm" variant="ghost" asChild disabled={uploading}>
                      <span>
                        {uploading ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Upload className="w-4 h-4" />
                        )}
                      </span>
                    </Button>
                  </label>

                  {/* Share Toggle (Super Admin only) */}
                  {isSuperAdmin && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleToggleShare(po)}
                      title={po.share_with_hospital ? "Hide from hospital" : "Share with hospital"}
                    >
                      {po.share_with_hospital ? (
                        <Unlock className="w-4 h-4 text-emerald-600" />
                      ) : (
                        <Lock className="w-4 h-4 text-slate-500" />
                      )}
                    </Button>
                  )}

                  {/* Delete (Super Admin only) */}
                  {isSuperAdmin && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-red-600 hover:bg-red-50"
                      onClick={() => {
                        setSelectedPO(po);
                        setShowDeleteConfirm(true);
                      }}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add PO Modal */}
      <Dialog open={showAddPO} onOpenChange={setShowAddPO}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-600" />
              Add Purchase Order
            </DialogTitle>
            <DialogDescription>
              Add a purchase order for {entityName || `this ${entityType}`}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label>PO Number *</Label>
              <Input
                value={formData.po_number}
                onChange={(e) => setFormData({ ...formData, po_number: e.target.value })}
                placeholder="e.g. PO-2024-001234"
              />
            </div>

            <div>
              <Label>Hospital Name</Label>
              <Input
                value={formData.hospital_name}
                onChange={(e) => setFormData({ ...formData, hospital_name: e.target.value })}
                placeholder="e.g. Harris Health System"
              />
            </div>

            <div>
              <Label>Amount</Label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  type="number"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  placeholder="0.00"
                  className="pl-10"
                />
              </div>
            </div>

            <div>
              <Label>Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Additional details about this purchase order..."
                rows={3}
              />
            </div>

            {isSuperAdmin && (
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div>
                  <p className="font-medium text-slate-700">Share with Hospital</p>
                  <p className="text-xs text-slate-500">Allow hospital staff to view this PO</p>
                </div>
                <Switch
                  checked={formData.share_with_hospital}
                  onCheckedChange={(checked) =>
                    setFormData({ ...formData, share_with_hospital: checked })
                  }
                />
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddPO(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddPO} disabled={submitting}>
              {submitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Add Purchase Order
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Purchase Order?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete PO "{selectedPO?.po_number}"? This action cannot be
              undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              disabled={submitting}
            >
              {submitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
